import React from "react";
import { Col, Row } from "reactstrap";
import { InfoOutlined } from "@mui/icons-material";
import { AlertColor } from "@mui/material";
import FormInput from "../../../../../../toolkit/formInput";
import WizardFormStep from "../../components/WizardFormStep";
import PrimaryButton from "../../../../../../toolkit/primaryButton";
import * as yup from "yup";
import { createUrlField } from "../../../../../../utils/validation/validationUtils";
import { useLoading } from "../../../../../../hooks/useLoading";
import { useFormContext } from "react-hook-form";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import { savePaymentSetupData } from "../../../../../../utils/firebase/paymentSetup";
import { setCustomDonationUrl } from "../../../../../../utils/fetch";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";

export const customUrlInputSchema = yup.object({
  customDonationUrl: createUrlField("Donation URL"),
});

interface CustomUrlInputProps extends ServiceCallerProps {
  onExit?: () => void;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
  onBack?: () => void;
}

const CustomUrlInput: React.FC<CustomUrlInputProps> = ({
  currentCharity,
  config,
  serviceAccessToken,
  onExit,
  showSnackbar,
  onBack,
}) => {
  const { loading: isSaving, runWithLoading } = useLoading();
  const { setValue, trigger, getValues } = useFormContext();

  const handleSave = async () => {
    const isValid = await trigger("customDonationUrl");
    if (!isValid) return;

    const formData = getValues();
    await handleSubmit(formData);
  };

  const handleSubmit = async (formData: any) => {
    await runWithLoading(async () => {
      try {
        let cleanUrl = formData.customDonationUrl.trim();
        if (!/^https?:\/\//i.test(cleanUrl)) {
          cleanUrl = `https://${cleanUrl}`;
        }
        if (!currentCharity?.id) {
          throw new Error("No charity ID available");
        }
        if (!config?.adminServiceUrl || !serviceAccessToken) {
          throw new Error("Missing admin service configuration");
        }

        await setCustomDonationUrl(
          currentCharity.id,
          config.adminServiceUrl,
          serviceAccessToken,
          cleanUrl
        );

        await savePaymentSetupData(currentCharity.id, {
          paymentMethod: "customUrl",
          customDonationUrl: cleanUrl,
          progressTracker: "url_configured",
        });

        setValue("customDonationUrl", cleanUrl);

        if (showSnackbar) {
          showSnackbar("Your custom donation URL has been successfully configured!", "success");
        }

        setTimeout(() => {
          if (onExit) {
            onExit();
          }
        }, 3000);

      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        console.error("Failed to save custom donation URL:", error);
        throw new Error(
          `Failed to save donation URL: ${errorMessage}. Please try again.`
        );
      }
    });
  };

  return (
    <WizardFormStep
      title="Enter Your Donation URL"
      subtitle="Provide the link where donors can make contributions to your organization"
    >
      <Row className="mt-4 justify-content-center">
        <Col md="8">
          <FormInput
            name="customDonationUrl"
            type="url"
            icon="fa-solid fa-link"
            placeholder="https://your-website.com/donate"
          />
          <div className="mt-3">
            <div className={s.infoText}>
              <InfoOutlined className={s.infoIcon} />
              <span>
                This URL will be used to direct donors to your existing donation platform.
                Make sure the URL is active and accepts donations.
              </span>
            </div>
          </div>

          {onBack && (
            <div className={`mt-4 text-center ${s.alternativeText}`}>
              <span>
                Want to use our built-in payment solution?{" "}
                <button
                  type="button"
                  className={s.alternativeLink}
                  onClick={onBack}
                >
                  Set up GiveSpark instead
                </button>
              </span>
            </div>
          )}
        </Col>
      </Row>

      <Row className="justify-content-center mt-4 mb-4">
        <Col md={8} className="d-flex justify-content-center">
          <PrimaryButton
            onClick={handleSave}
            loading={isSaving}
            loadingText="Saving..."
            loadingAnimation="pulse"
            variant="primary"
          >
            Save Donation URL
          </PrimaryButton>
        </Col>
      </Row>
    </WizardFormStep>
  );
};

export default serviceCaller(withStyles(s)(CustomUrlInput));